
import { useState, useEffect, useRef, useCallback } from 'react';

interface Testimonial {
  id: number;
  name: string;
  role: string;
  company: string;
  image: string;
  quote: string;
  rating: number;
  project: string;
}

export default function Testimonials() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const sectionRef = useRef<HTMLDivElement>(null);
  const carouselRef = useRef<HTMLDivElement>(null);

  const testimonials: Testimonial[] = [
    {
      id: 1,
      name: 'Chukwuemeka Okonkwo',
      role: 'CEO',
      company: 'Okonkwo Holdings',
      image: 'https://readdy.ai/api/search-image?query=professional%20portrait%20of%20confident%20Black%20African%20businessman%20in%20elegant%20dark%20suit%2C%20warm%20studio%20lighting%2C%20sophisticated%20corporate%20headshot%2C%20neutral%20background%2C%20sharp%20focus%20on%20face%2C%20editorial%20photography%20style%2C%20Nigerian%20executive&width=400&height=400&seq=test001&orientation=squarish',
      quote: 'BuildFast transformed our Banana Island residence into something beyond our imagination. The attention to detail in every corner, from the custom millwork to the marble finishing, speaks volumes about their craftsmanship. They delivered perfection.',
      rating: 5,
      project: 'Banana Island Residence'
    },
    {
      id: 2,
      name: 'Adebayo Fashola',
      role: 'Managing Director',
      company: 'Fashola Developments',
      image: 'https://readdy.ai/api/search-image?query=professional%20portrait%20of%20distinguished%20Black%20African%20man%20in%20tailored%20navy%20blazer%2C%20confident%20smile%2C%20warm%20lighting%2C%20corporate%20executive%20headshot%2C%20clean%20background%2C%20high%20end%20photography%2C%20Lagos%20businessman&width=400&height=400&seq=test002&orientation=squarish',
      quote: 'Working with BuildFast on our hotel project was exceptional. Their team understood our vision for luxury hospitality and executed it flawlessly. The lobby alone has become a talking point for every guest who walks through our doors.',
      rating: 5,
      project: 'Azure Hotel Victoria Island'
    },
    {
      id: 3,
      name: 'Olumide Adeyemi',
      role: 'Founder',
      company: 'Adeyemi Capital',
      image: 'https://readdy.ai/api/search-image?query=professional%20portrait%20of%20successful%20Black%20African%20entrepreneur%20in%20premium%20charcoal%20suit%2C%20warm%20ambient%20lighting%2C%20sophisticated%20business%20headshot%2C%20minimalist%20background%2C%20sharp%20detailed%20photography%2C%20Nigerian%20business%20leader&width=400&height=400&seq=test003&orientation=squarish',
      quote: 'The executive suite they designed for our Ikoyi office has elevated our entire brand perception. Clients walk in and immediately understand the level of excellence we represent. BuildFast does not just finish spaces—they create experiences.',
      rating: 5,
      project: 'Sterling Executive Suite'
    },
    {
      id: 4,
      name: 'Tunde Bakare',
      role: 'Chairman',
      company: 'Bakare Group',
      image: 'https://readdy.ai/api/search-image?query=professional%20portrait%20of%20elegant%20Black%20African%20gentleman%20in%20bespoke%20gray%20suit%2C%20distinguished%20appearance%2C%20warm%20studio%20lighting%2C%20executive%20corporate%20photography%2C%20refined%20background%2C%20Nigerian%20business%20magnate&width=400&height=400&seq=test004&orientation=squarish',
      quote: 'After working with several contractors over the years, BuildFast stands apart. Their commitment to quality is unwavering. Our penthouse in Lekki Phase 1 is a testament to what happens when you trust true craftsmen with your vision.',
      rating: 5,
      project: 'Lekki Penthouse'
    },
    {
      id: 5,
      name: 'Emeka Nwosu',
      role: 'Director',
      company: 'Nwosu Enterprises',
      image: 'https://readdy.ai/api/search-image?query=professional%20portrait%20of%20confident%20Black%20African%20business%20executive%20in%20dark%20tailored%20suit%2C%20warm%20professional%20lighting%2C%20corporate%20headshot%20photography%2C%20clean%20modern%20background%2C%20Nigerian%20entrepreneur%20portrait&width=400&height=400&seq=test005&orientation=squarish',
      quote: 'BuildFast delivered our private members club ahead of schedule without compromising an inch on quality. The bespoke bar area and custom millwork have made our space the most sought-after venue in Lagos. Exceptional work.',
      rating: 5,
      project: 'The Members Club Lagos'
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Auto-play functionality
  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, testimonials.length]);

  const handleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    setIsDragging(true);
    setIsAutoPlaying(false);
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    setStartX(clientX);
  };

  const handleDragMove = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (!isDragging) return;
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const diff = clientX - startX;
    setDragOffset(diff);
  }, [isDragging, startX]);

  const handleDragEnd = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);
    
    const threshold = 80;
    if (dragOffset > threshold && activeIndex > 0) {
      setActiveIndex(activeIndex - 1);
    } else if (dragOffset < -threshold && activeIndex < testimonials.length - 1) {
      setActiveIndex(activeIndex + 1);
    }
    
    setDragOffset(0);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  }, [isDragging, dragOffset, activeIndex, testimonials.length]);

  const goToSlide = (index: number) => {
    setActiveIndex(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const nextSlide = () => {
    setActiveIndex((prev) => (prev + 1) % testimonials.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const prevSlide = () => {
    setActiveIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  return (
    <section ref={sectionRef} className="w-full bg-[#0A0A0A] py-16 md:py-24 lg:py-32 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-8 lg:px-16">
        {/* Header */}
        <div className={`text-center mb-12 md:mb-16 lg:mb-20 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div className="flex items-center justify-center gap-3 md:gap-4 mb-4 md:mb-6">
            <div className="w-8 md:w-12 h-px bg-[#C9A86C]" />
            <span className="text-[#C9A86C] text-xs md:text-sm tracking-[0.2em] md:tracking-[0.3em] uppercase">Testimonials</span>
            <div className="w-8 md:w-12 h-px bg-[#C9A86C]" />
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-serif text-white mb-4 md:mb-6">
            Client <span className="text-[#C9A86C]">Voices</span>
          </h2>
          <p className="text-white/50 max-w-xl mx-auto text-sm md:text-base">
            Hear from those who have experienced the BuildFast difference firsthand.
          </p>
        </div>

        {/* Carousel Container */}
        <div className={`relative transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          {/* Main Carousel */}
          <div 
            ref={carouselRef}
            className="relative cursor-grab active:cursor-grabbing select-none"
            onMouseDown={handleDragStart}
            onMouseMove={handleDragMove}
            onMouseUp={handleDragEnd}
            onMouseLeave={handleDragEnd}
            onTouchStart={handleDragStart}
            onTouchMove={handleDragMove}
            onTouchEnd={handleDragEnd}
          >
            <div className="overflow-hidden">
              <div 
                className="flex transition-transform duration-500 ease-out"
                style={{ 
                  transform: `translateX(calc(-${activeIndex * 100}% + ${dragOffset}px))`,
                  transition: isDragging ? 'none' : 'transform 0.5s ease-out'
                }}
              >
                {testimonials.map((testimonial, index) => (
                  <div 
                    key={testimonial.id} 
                    className="w-full flex-shrink-0 px-2 md:px-4"
                  >
                    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 md:gap-8 lg:gap-12 items-center">
                      {/* Image Side */}
                      <div className="lg:col-span-2 flex justify-center lg:justify-end">
                        <div className="relative">
                          <div className={`w-48 h-48 sm:w-56 sm:h-56 md:w-64 md:h-64 lg:w-72 lg:h-72 rounded-full overflow-hidden border-4 border-[#C9A86C]/30 transition-all duration-700 ${
                            activeIndex === index ? 'scale-100 opacity-100' : 'scale-90 opacity-50'
                          }`}>
                            <img
                              src={testimonial.image}
                              alt={testimonial.name}
                              className="w-full h-full object-cover object-top"
                              draggable={false}
                            />
                          </div>
                          {/* Decorative ring */}
                          <div className={`absolute inset-0 rounded-full border-2 border-[#C9A86C] transition-all duration-700 ${
                            activeIndex === index ? 'scale-110 opacity-100' : 'scale-100 opacity-0'
                          }`} />
                          {/* Quote icon */}
                          <div className={`absolute -bottom-2 -right-2 md:-bottom-4 md:-right-4 w-12 h-12 md:w-16 md:h-16 bg-[#C9A86C] rounded-full flex items-center justify-center transition-all duration-500 ${
                            activeIndex === index ? 'scale-100 opacity-100' : 'scale-0 opacity-0'
                          }`}>
                            <i className="ri-double-quotes-r text-xl md:text-2xl text-[#0A0A0A]" />
                          </div>
                        </div>
                      </div>

                      {/* Content Side */}
                      <div className="lg:col-span-3 text-center lg:text-left">
                        {/* Rating */}
                        <div className={`flex items-center justify-center lg:justify-start gap-1 mb-4 md:mb-6 transition-all duration-500 delay-100 ${
                          activeIndex === index ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                        }`}>
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <i key={i} className="ri-star-fill text-[#C9A86C] text-lg md:text-xl" />
                          ))}
                        </div>

                        {/* Quote */}
                        <blockquote className={`text-lg sm:text-xl md:text-2xl lg:text-3xl font-serif text-white leading-relaxed mb-6 md:mb-8 transition-all duration-500 delay-200 ${
                          activeIndex === index ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                        }`}>
                          &ldquo;{testimonial.quote}&rdquo;
                        </blockquote>

                        {/* Author Info */}
                        <div className={`transition-all duration-500 delay-300 ${
                          activeIndex === index ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                        }`}>
                          <h4 className="text-xl md:text-2xl font-serif text-[#C9A86C] mb-1">{testimonial.name}</h4>
                          <p className="text-white/60 text-sm md:text-base">{testimonial.role}, {testimonial.company}</p>
                          <p className="text-white/40 text-xs md:text-sm mt-2 flex items-center justify-center lg:justify-start gap-2">
                            <i className="ri-building-line" />
                            {testimonial.project}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Navigation Arrows */}
          <div className="hidden md:flex items-center justify-between absolute top-1/2 -translate-y-1/2 left-0 right-0 pointer-events-none">
            <button 
              onClick={prevSlide}
              className="w-12 h-12 lg:w-14 lg:h-14 bg-white/5 hover:bg-[#C9A86C] border border-white/10 hover:border-[#C9A86C] flex items-center justify-center transition-all duration-300 pointer-events-auto cursor-pointer group -ml-2 lg:-ml-6"
            >
              <i className="ri-arrow-left-line text-xl text-white group-hover:text-[#0A0A0A] transition-colors" />
            </button>
            <button 
              onClick={nextSlide}
              className="w-12 h-12 lg:w-14 lg:h-14 bg-white/5 hover:bg-[#C9A86C] border border-white/10 hover:border-[#C9A86C] flex items-center justify-center transition-all duration-300 pointer-events-auto cursor-pointer group -mr-2 lg:-mr-6"
            >
              <i className="ri-arrow-right-line text-xl text-white group-hover:text-[#0A0A0A] transition-colors" />
            </button>
          </div>

          {/* Dots Navigation */}
          <div className="flex items-center justify-center gap-3 mt-8 md:mt-12">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`relative h-2 transition-all duration-500 cursor-pointer ${
                  activeIndex === index ? 'w-10 md:w-12' : 'w-2'
                }`}
              >
                <span className={`absolute inset-0 rounded-full transition-colors duration-300 ${
                  activeIndex === index ? 'bg-[#C9A86C]' : 'bg-white/20 hover:bg-white/40'
                }`} />
                {activeIndex === index && isAutoPlaying && (
                  <span 
                    className="absolute inset-0 rounded-full bg-white/30 origin-left animate-progress"
                    style={{ animation: 'progress 5s linear' }}
                  />
                )}
              </button>
            ))}
          </div>

          {/* Mobile Swipe Hint */}
          <p className="text-center text-white/30 text-xs mt-6 md:hidden flex items-center justify-center gap-2">
            <i className="ri-hand-coin-line" />
            Swipe to navigate
          </p>
        </div>

        {/* Client Logos/Trust Indicators */}
        <div className={`mt-16 md:mt-20 pt-12 md:pt-16 border-t border-white/10 transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <p className="text-center text-white/40 text-xs md:text-sm tracking-widest uppercase mb-8">Trusted by Industry Leaders</p>
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12 lg:gap-16">
            {['Okonkwo Holdings', 'Fashola Developments', 'Adeyemi Capital', 'Bakare Group', 'Nwosu Enterprises'].map((company, index) => (
              <span 
                key={index} 
                className="text-white/20 hover:text-[#C9A86C] text-sm md:text-base font-serif transition-colors duration-300 cursor-pointer"
              >
                {company}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Progress Animation Keyframes */}
      <style>{`
        @keyframes progress {
          from { transform: scaleX(0); }
          to { transform: scaleX(1); }
        }
        .animate-progress {
          animation: progress 5s linear;
        }
      `}</style>
    </section>
  );
}
